import { SwapOutlined } from "@ant-design/icons";
import { Collapse, Form } from "antd";
import ActivityOffsetTag from "components/activity-offset-tag";
import CriticalActivityIndicator from "components/links/critical-activity-indicator";
import AsterixIcon from "components/svg-icons/asterix-icon";
import LinkIcon from "components/svg-icons/link-icon";
import { FeatureTypes } from "entity-app/constants";
import { useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import { DateUtils } from "utils/dateutils";
import { sortAlphanumeric } from "utils/utils";

export default function FeatureActivitiesLinks(props: {
  featureInstanceData: any;
  canEditLinkingPage: boolean;
  isRevisionMode?: boolean;
  isVoidSubmittal: boolean;
  isDeletedSubmittal: boolean;
}) {
  const {
    featureInstanceData,
    canEditLinkingPage,
    isRevisionMode = false,
    isVoidSubmittal,
    isDeletedSubmittal
  } = props;
  const { projectId } = useParams() as any;
  const { Panel } = Collapse;

  const getLinkedToEndDate = (
    link_type: number,
    reverse_dependency: boolean
  ) => {
    if (link_type === 2 && !reverse_dependency) {
      return true;
    }
    if (link_type === 0 && reverse_dependency) {
      return true;
    }
    return false;
  };

  const linkedActivities = useMemo(() => {
    const mappedItems = featureInstanceData?.links_as_source
      ?.filter(
        (link: any) =>
          link?.target_feature_instance?.feature_type_id ===
          FeatureTypes.ACTIVITIES
      )
      .map((link: any) => {
        return {
          ...link?.target_feature_instance,
          link_type: link?.link_type,
          linked_to_end_date: getLinkedToEndDate(
            link?.link_type,
            link?.reverse_dependency
          ),
          governing_link: link?.governing_link,
          linked_offset_date: link?.linked_offset_date,
          offset_duration: link?.offset_duration,
          reverse_dependency: link?.reverse_dependency
        };
      });

    if (featureInstanceData?.feature_type_id === FeatureTypes.MATERIALS) {
      const linkedTasks = featureInstanceData?.links_as_target
        ?.filter(
          (link: any) =>
            link?.source_feature_instance?.feature_type_id ===
            FeatureTypes.ACTIVITIES
        )
        .map((link: any) => {
          return {
            ...link?.source_feature_instance,
            link_type: link?.link_type,
            linked_to_end_date: getLinkedToEndDate(
              link?.link_type,
              link?.reverse_dependency
            ),
            governing_link: link?.governing_link,
            linked_offset_date: link?.linked_offset_date,
            offset_duration: link?.offset_duration,
            reverse_dependency: link?.reverse_dependency
          };
        });
      mappedItems.push(...linkedTasks);
    }

    return sortAlphanumeric(mappedItems || [], "number");
  }, [
    featureInstanceData?.feature_type_id,
    featureInstanceData?.links_as_source,
    featureInstanceData?.links_as_target
  ]);

  const schedulePanelHeader = (
    link: any,
    featureActivities: any,
    isPermissionEnabled: boolean
  ) => {
    const headerMarkup = (
      <div className="flex-col" data-testid="fal-header-container">
        <div
          className="flex items-center"
          title={`${link?.number}-${link?.title}`}
          data-testid="fal-header-row"
        >
          {isPermissionEnabled && featureActivities?.critical ? (
            <span className="flex mr-2" data-testid="fal-critical-indicator">
              <CriticalActivityIndicator data-testid="fal-critical-icon" />
            </span>
          ) : (
            <span
              className="flex items-center mr-2"
              data-testid="fal-link-indicator"
            >
              <LinkIcon data-testid="fal-link-icon" />
            </span>
          )}
          <i className="opacity-60" data-testid="fal-activity-number">
            {link?.number}
            {link?.number && !link.governing_link && (
              <span className="mx-1" data-testid="fal-number-separator">
                -
              </span>
            )}
          </i>
          {link.governing_link && (
            <span
              className="flex items-center mx-1 mb-1"
              data-testid="fal-governing-link"
            >
              <AsterixIcon data-testid="fal-governing-icon" />
            </span>
          )}
          <span
            className="flex-grow truncate mr-1"
            data-testid="fal-activity-title"
          >
            {link?.title || ""}
          </span>

          {(featureActivities?.start_date || featureActivities?.end_date) && (
            <i
              className={`${
                link?.linked_offset_date ? "mr-2" : "mr-16"
              } opacity-60 px-1 w-[95px]`}
              data-testid="fal-activity-dates"
            >
              {featureActivities?.start_date && !link?.linked_to_end_date
                ? `Start: ${DateUtils.format(featureActivities?.start_date)}`
                : null}
              {featureActivities?.end_date && link?.linked_to_end_date
                ? `End: ${DateUtils.format(featureActivities?.end_date)}`
                : null}
            </i>
          )}
          {link?.linked_offset_date && (
            <span className="mr-5 ml-9" data-testid="fal-offset-container">
              <ActivityOffsetTag
                task={{
                  ...featureActivities,
                  linked_offset_date: link?.linked_offset_date,
                  offset_duration: link?.offset_duration
                }}
                isShowDate
                data-testid="fal-offset-tag"
              />
            </span>
          )}
        </div>
        {link?.reverse_dependency && (
          <div
            className="flex items-center bg-ciq-gray-500 rounded h-6 space-x-1 px-2 w-[186px]"
            data-testid="fal-reverse-dependency"
          >
            <span className="flex items-center">
              <SwapOutlined
                className="text-ciq-gray-550"
                data-testid="fal-swap-icon"
              />
            </span>
            <span className="flex items-center font-normal text-xs">
              Activity is the Predecessor
            </span>
          </div>
        )}
      </div>
    );

    return headerMarkup;
  };

  return (
    <Form layout="vertical" data-testid="fal-form">
      <Form.Item
        label={
          <span data-testid="fal-label">
            Schedule Activities
            {linkedActivities?.length ? ` (${linkedActivities?.length})` : ""}
          </span>
        }
        data-testid="fal-form-item"
      >
        {linkedActivities?.length > 0 ? (
          <Collapse
            accordion
            expandIconPosition="end"
            className="custom-collapse"
            data-testid="fal-collapse"
          >
            {linkedActivities?.map((link: any) => {
              if (!link?.feature_activities?.length) {
                return null; // Skip if feature_activities is not present
              }
              const featureActivity = link?.feature_activities[0];
              const floatVal = featureActivity?.total_slack
                ? Math.floor((featureActivity?.total_slack || 0) / 8)
                : "";
              const isPermissionEnabled =
                featureActivity?.total_slack_computed !== "restricted";
              return (
                <Panel
                  header={schedulePanelHeader(
                    link,
                    featureActivity,
                    isPermissionEnabled
                  )}
                  key={link.task_id}
                  className={`custom-panel ${
                    link?.reverse_dependency
                      ? "custom-panel-arrow-icon-with-reverse-dependency"
                      : "custom-panel-arrow-icon-without-reverse-dependency"
                  }`}
                  data-testid="fal-panel"
                >
                  <div
                    className="w-full text-[10px] px-2"
                    data-testid="fal-activity-details"
                  >
                    <div className="flex w-full" data-testid="fal-dates-row">
                      <div
                        className="flex w-full space-x-[1px]"
                        data-testid="fal-planned-date"
                      >
                        <span className="text-ciq-gray-440 font-medium">
                          {link?.linked_to_end_date
                            ? "Planned End Date:"
                            : "Planned Start Date:"}
                        </span>
                        <span className="text-black text-opacity-80">
                          {link?.linked_to_end_date
                            ? DateUtils.format(
                                featureActivity.end_date,
                                "MM-DD-YYYY"
                              )
                            : DateUtils.format(
                                featureActivity.start_date,
                                "MM-DD-YYYY"
                              )}
                        </span>
                        {link?.offset_duration && (
                          <span data-testid="fal-offset-tag">
                            <ActivityOffsetTag
                              task={{
                                ...featureActivity,
                                linked_offset_date: link?.linked_offset_date,
                                offset_duration: link?.offset_duration
                              }}
                              isShowDate={false}
                            />
                          </span>
                        )}
                      </div>
                      {featureActivity?.actual_start_date && (
                        <div
                          className="flex w-full space-x-1"
                          data-testid="fal-actual-start-date"
                        >
                          <span className="text-ciq-gray-440 font-medium ml-6">
                            Actual Start Date:
                          </span>
                          <span className="text-black text-opacity-80">
                            {DateUtils.format(
                              featureActivity?.actual_start_date,
                              "MM-DD-YYYY"
                            )}
                          </span>
                        </div>
                      )}
                    </div>
                    <div className="flex w-full" data-testid="fal-progress-row">
                      {isPermissionEnabled && (
                        <div
                          className="flex w-full space-x-1"
                          data-testid="fal-activity-float"
                        >
                          <span className="text-ciq-gray-440 font-medium">
                            Activity Float:
                          </span>
                          <span
                            className={`text-black text-opacity-80 ${
                              featureActivity?.critical ? "font-bold" : ""
                            }`}
                          >
                            {floatVal !== "" ? `${floatVal} days` : ""}
                          </span>
                        </div>
                      )}
                      {featureActivity?.progress > 0 &&
                        featureActivity?.progress < 100 &&
                        !featureActivity?.actual_finish_date && (
                          <div
                            className="flex w-full space-x-1"
                            data-testid="fal-progress"
                          >
                            <span className="text-ciq-gray-440 font-medium ml-6">
                              Progress:
                            </span>
                            <span className="text-black text-opacity-80 w-full">
                              {`${featureActivity?.progress}%`}
                            </span>
                          </div>
                        )}
                      {featureActivity?.progress === 100 &&
                        featureActivity?.actual_finish_date && (
                          <div
                            className="flex w-full space-x-1"
                            data-testid="fal-actual-end-date"
                          >
                            <span className="text-ciq-gray-440 font-medium ml-6">
                              Actual End Date:
                            </span>
                            <span className="text-black text-opacity-80">
                              {DateUtils.format(
                                featureActivity?.actual_finish_date,
                                "MM-DD-YYYY"
                              )}
                            </span>
                          </div>
                        )}
                    </div>
                  </div>
                </Panel>
              );
            })}
          </Collapse>
        ) : (
          <span className="text-xs text-neutral-400" data-testid="fal-no-links">
            No links added{" "}
            {!isVoidSubmittal &&
              !isDeletedSubmittal &&
              canEditLinkingPage &&
              !isRevisionMode &&
              (featureInstanceData?.feature?.id === FeatureTypes.SUBMITTALS ? (
                <Link
                  to={`/project/${projectId}/schedule/submittal-linking?&SubmittalId=${featureInstanceData?.id}&source=submittal`}
                  data-testid="fal-link-to-add-submittal"
                >
                  Click to add
                </Link>
              ) : (
                <Link
                  to={`/project/${projectId}/schedule/material-linking?&materialId=${featureInstanceData?.id}`}
                  data-testid="fal-link-to-add-material"
                >
                  Click to add
                </Link>
              ))}
          </span>
        )}
      </Form.Item>
    </Form>
  );
}
